import { inject, Injectable } from "@angular/core";
import { Firestore, query, getDocs, collection, where, Timestamp, doc, setDoc, getDoc, orderBy, updateDoc, arrayUnion } from "@angular/fire/firestore";
import { AssessmentQuestion, ImageOptionsQuestion, TextOptionsQuestion } from "../models/assessment/assessment-question.model";
import { TenantService } from "./tenant.service";
import { AssessmentAttempt, LatestAssessmentAttempt } from "../models/assessment/assessment-attempt.model";
import { createFirestoreId } from "../common/firestore-utils";
import { AssessmentAnswer, ImageOptionsAssessmentAnswer, TextOptionsAssessmentAnswer } from "../models/assessment/assessment-answer.model";
import { Lesson } from "../models/lesson.model";
import { MessagesService } from '../shared/services/messages.service';
import { QuestionBankService } from "./question-bank.service";

@Injectable({
  providedIn: 'root'
})
export class AssessmentService {
  private firestore: Firestore = inject(Firestore);
  private tenant: TenantService = inject(TenantService);
  private messagesService: MessagesService = inject(MessagesService);
  private questionBankService: QuestionBankService = inject(QuestionBankService);


  constructor() { }

  /**
   * Calculates the partial score for a text-options or image-options question based on correct and incorrect selections.
   * 
   * @param userAnswer The user's answer containing selected options
   * @param question The question containing all available options with correct/incorrect flags
   * @returns A score between 0 and 1
   */
  calculatePartialScore(userAnswer: TextOptionsAssessmentAnswer | ImageOptionsAssessmentAnswer, question: AssessmentQuestion): number {
    if (!userAnswer.answer || userAnswer.answer.length === 0) {
      return 0;
    }

    let correctAnswerIds: Set<string> = new Set();
    // Get all correct answer IDs from the question
    if (question.type === 'text-options') {
      correctAnswerIds = new Set(
        (question as TextOptionsQuestion).textOptions.filter(option => option.isCorrect).map(option => option.id)
      );
    } else if (question.type === 'image-options') {
      correctAnswerIds = new Set(
        (question as ImageOptionsQuestion).imageOptions.filter(option => option.isCorrect).map(option => option.id)
      );
    }

    const totalCorrectAnswers = correctAnswerIds.size;

    if (totalCorrectAnswers === 0) {
      return 0; // No correct answers defined, return 0
    }

    // Count correct and incorrect selections
    let correctSelected = 0;
    let incorrectSelected = 0;

    userAnswer.answer.forEach(selectedOption => {
      if (correctAnswerIds.has(selectedOption.id)) {
        correctSelected++;
      } else {
        incorrectSelected++;
      }
    });

    /**
       * Lenient scoring logic:
       * - Start from the base fraction of correct selections
       * - Apply a smaller penalty for incorrect picks (half weight) (incorrectSelected / (2 * totalCorrectAnswers))
       * - Never drop below 0, never exceed 1
       *****earlier  In (correct - incorrect) / total correct, each wrong choice subtracts a full fraction of a correct one:
       */
    let score = (correctSelected / totalCorrectAnswers) - (incorrectSelected / (2 * totalCorrectAnswers));
    score = Math.max(0, Math.min(1, Math.round(score * 100) / 100));
    
    return score;
  }

  /**
   * Get all assessment questions for a tenant, ordered by their order field
   */
async getLessonAssessmentQuestions(
  tenantId: string,
  lessonId: string,
  courseId: string,
  attemptId?: string
): Promise<AssessmentQuestion[]> {
  try {
    const lessonRef = doc(this.firestore, `schools/${tenantId}/courses/${courseId}/lessons/${lessonId}`);
    const lessonDoc = await getDoc(lessonRef);

    if (!lessonDoc.exists()) {
      return [];
    }

    const lessonData = lessonDoc.data() as Lesson;

    if (lessonData?.type === 'random-assessment') {
      return this.fetchRandomAssessmentQuestions(tenantId, lessonData, attemptId);
    } else {
      return this.fetchFixedAssessmentQuestions(tenantId, lessonData);
    }
  } catch (error) {
    console.error('Error fetching lesson assessment questions:', error);
    return [];
  }
}

/**
 * 1. Retrieves questions for a fixed assessment based on pre-defined IDs.
 */
private async fetchFixedAssessmentQuestions(
  tenantId: string,
  lessonData: Lesson
): Promise<AssessmentQuestion[]> {
  const questionIds: string[] = lessonData?.assessment?.assessmentQuestionIds || [];

  if (questionIds.length === 0) {
    return [];
  }

  const questionsMap = await this.getQuestionsByIds(tenantId, questionIds);

  // Map and filter to preserve order and remove missing questions
  const orderedQuestions = questionIds
    .map((id) => questionsMap.get(id))
    .filter((q): q is AssessmentQuestion => !!q);

  return orderedQuestions;
}

/**
 * 2. Retrieves questions for a random assessment, prioritizing existing attempt data.
 */
private async fetchRandomAssessmentQuestions(
  tenantId: string,
  lessonData: Lesson,
  attemptId?: string
): Promise<AssessmentQuestion[]> {
  let attemptQuestionIds: string[] = [];

  // Check for existing attempt
  if (attemptId) {
    const attemptDocRef = doc(this.firestore, `schools/${tenantId}/latestAssessmentAttempts/${attemptId}`);
    const attemptDoc = await getDoc(attemptDocRef);
    const attemptData = attemptDoc.data() as AssessmentAttempt;

    // Use existing questions only if the array is non-empty
    if (attemptData?.assessmentQuestionIds?.length) {
      attemptQuestionIds = attemptData.assessmentQuestionIds;
    }
  }

  // If existing questions found, fetch them
  if (attemptQuestionIds.length > 0) {
    const questionsMap = await this.getQuestionsByIds(tenantId, attemptQuestionIds);
    return attemptQuestionIds
      .map((id) => questionsMap.get(id))
      .filter((q): q is AssessmentQuestion => !!q);
  }

  // If no existing attempt or questions, generate new random questions
  if (!lessonData.assessment?.selectionRules?.length) {
    console.warn('Random assessment configured without selection rules.');
    return [];
  }

  const rulesFilter = []
  lessonData.assessment.selectionRules.forEach(async element => {
  rulesFilter.push({
  tenantId: tenantId,
  tags: element.tags.join(','),
  questionTypes: element.questionType.map(i=>i.type).join(','),
  limit: element.limit
  });
});

return await this.questionBankService.getRandomQuestionsList(rulesFilter)
}

private async getQuestionsByIds(
  tenantId: string,
  questionIds: string[]
): Promise<Map<string, AssessmentQuestion>> {
  const questionsMap = new Map<string, AssessmentQuestion>();
  const BATCH_SIZE = 10;
  
  // Use map and Promise.all to fetch batches in parallel
  const questionIdBatches: string[][] = [];
  for (let i = 0; i < questionIds.length; i += BATCH_SIZE) {
    questionIdBatches.push(questionIds.slice(i, i + BATCH_SIZE));
  }
  
  const batchPromises = questionIdBatches.map(batch => 
    getDocs(
      query(
        collection(this.firestore, `schools/${tenantId}/assessmentQuestions`),
        where('id', 'in', batch),
        where('isDeleted', '==', false),
      )
    )
  );

  const batchQuerySnapshots = await Promise.all(batchPromises);

  batchQuerySnapshots.forEach(snapshot => {
    snapshot.forEach(doc => {
      questionsMap.set(doc.id, { id: doc.id, ...doc.data() } as AssessmentQuestion);
    });
  });

  return questionsMap;
}
  /**
   * Creates a new assessment attempt.
   * @param userId The ID of the user attempting the assessment.
   * @param assessmentId The ID of the assessment.
   * @returns A Promise that resolves with the ID of the created attempt.
   */
  async startAssessmentAttempt(userId: string, assessmentId: string,totalQuestions: number, courseId: string, attemptNumber: number, lessonType?: string, assessmentQuestions?:Array<string>): Promise<string> {
    const attemptId = createFirestoreId(this.firestore);
    const startedAt = Timestamp.now();

    const newAttempt: AssessmentAttempt = {
      id: attemptId,
      userId: userId,
      assessmentId: assessmentId,
      courseId: courseId,
      startedAt: startedAt,
      submittedAt: null, // Initialize, will be updated on submission
      timeSpent: 0,
      totalQuestions: totalQuestions,
      score: 0,
      passed: null,
      status: 'started',
      attemptNumber: attemptNumber,
      answers: [],
      isManuallyGraded: false,
      gradedBy: null,
      gradingNotes: '',
      manualScore: null,
    };

    if(lessonType === 'random-assessment'){
      newAttempt.assessmentQuestionIds = assessmentQuestions 
    }

    // Reference to the assessment attempt document
    const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);

    try {
      await setDoc(attemptDocRef, newAttempt);
      return attemptId;
    } catch (error) {
      throw error;
    }
  }

   /**
   * Submits an assessment attempt.
   * @param attemptId The ID of the attempt to submit.
   * @param answers The array of AssessmentAnswer objects.
   * @param timeSpent The total time spent on the assessment in seconds.
   * @param status The initial status of the attempt.
   * @param minimumPassingGrade The minimum passing grade percentage.
   * @param totalQuestions The total number of questions.
   * @param questions The array of assessment questions (needed for partial scoring).
   * @param isManualGradingRequired Whether manual grading is required.
   */
   async submitAssessmentAttempt(
    attemptId: string,
    answers: AssessmentAnswer[],
    timeSpent: number,
    status: string,
    minimumPassingGrade: number,
    totalQuestions: number,
    questions: AssessmentQuestion[],
    isManualGradingRequired?: boolean
  ): Promise<LatestAssessmentAttempt> {
    const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);
    try {
      // Calculate score, passed status based on userAnswers (auto-graded)
      // Create a map of questions for quick lookup
      const questionsMap = new Map<string, AssessmentQuestion>();
      questions.forEach(q => questionsMap.set(q.id, q));
      
      // Calculate score with partial scoring for text-options questions
      let score = 0;
      (answers || []).forEach(answer => {
        if (answer.type === 'text-options' || answer.type === 'image-options') {
          const question = questionsMap.get(answer.assessmentQuestionId);
          if (question) {
            score += this.calculatePartialScore(answer as TextOptionsAssessmentAnswer | ImageOptionsAssessmentAnswer, question as TextOptionsQuestion | ImageOptionsQuestion);
          }
        } else {
          // For other question types, use binary correct/incorrect
          score += answer.isCorrect ? 1 : 0;
        }
      });

      const passed = (score / totalQuestions) * 100 >= (minimumPassingGrade);

      if(passed) {
        status = 'completed';
      } else {
        status = 'failed';
      }

      await setDoc(attemptDocRef, {
        submittedAt: Timestamp.now(),
        gradedAt: isManualGradingRequired ? null : Timestamp.now(),
        timeSpent: timeSpent,
        status: isManualGradingRequired ? 'submitted' : status,
        answers: answers,
        score: isManualGradingRequired ? null : score,
        passed: isManualGradingRequired ? null : passed,
        totalQuestions: totalQuestions,
        // Fields for manual grading
        isManuallyGraded: isManualGradingRequired || false,
        manualScore: null,
        gradedBy: isManualGradingRequired ? null : 'auto',
        gradingNotes: ''
      }, { merge: true });

      return this.getAssessmentAttempt(attemptId);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Retrieves an assessment attempt by its ID.
   * @param attemptId The ID of the attempt.
   * @returns An Observable of the AssessmentAttempt, or null if not found.
   */
 async  getAssessmentAttempt(attemptId: string): Promise<LatestAssessmentAttempt> {
  try {
    const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);
    const snapshot = await getDoc(attemptDocRef);
    if (snapshot.exists()) {
      return { id: snapshot.id, ...(snapshot.data() as LatestAssessmentAttempt) };
    }
  } catch (error) {
    throw error;
  }
  }

  /**
   * Fetches the latest assessment attempt for a specific user and assessment.
   * This is used to determine the attemptNumber and previousAttempts.
   * @param userId The user's ID.
   * @param assessmentId The assessment's ID.
   * @returns The latest attempt or null if none exist.
   */
 async getLatestAttempt(userId: string, assessmentId: string){
  const attemptsCollectionRef = collection(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts`);

  const q = query(
    attemptsCollectionRef,
    where('userId', '==', userId),        // Filter by userId
    where('assessmentId', '==', assessmentId), // Filter by assessmentId
    orderBy('startedAt', 'desc')           // Order by startedAt descending
  );

  try {
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

      const latestDoc = querySnapshot.docs[0];

    // Return the single document data with its ID.
    return { id: latestDoc.id, ...(latestDoc.data() as LatestAssessmentAttempt) };

  } catch (error) {
    throw error; // Re-throw the error for the calling component to handle
  }
}


async getCourseAssessments(userId: string, courseId: string){
  const attemptsCollectionRef = collection(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts`);

  const q = query(
    attemptsCollectionRef,
    where('userId', '==', userId),        // Filter by userId
    where('courseId', '==', courseId), // Filter by courseId
    orderBy('startedAt', 'desc')           // Order by startedAt descending
  );

  try {
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

      const attempts = querySnapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as LatestAssessmentAttempt) }));

    return attempts;

  } catch (error) {
    throw error; // Re-throw the error for the calling component to handle
  }
}
  /**
   * Internal method to update the latestAssessmentAttempts collection.
   * This logic ensures previous attempts are stored correctly.
   */
  async updateExistingAttempt(latestAttemptId: string, newAttemptData: any): Promise<void> {
    const startedAt = Timestamp.now();
    const latestAttemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${latestAttemptId}`);
    // To archive the old attempt, we need to get its data first.
    const latestAttemptDocSnapshot = await getDoc(latestAttemptDocRef);
    if (!latestAttemptDocSnapshot.exists()) {
      throw new Error('Latest attempt document does not exist!');
    }
    
    const latestAttemptData = latestAttemptDocSnapshot.data() as LatestAssessmentAttempt;

    const { previousAttempts, ...latestWithoutHistory } = latestAttemptData;

    // The new attempt is a cleaned-up version of the current document.
    const newAttempt = {
      ...latestWithoutHistory,
      ...latestAttemptData,
      ...newAttemptData,
      startedAt: startedAt,
      submittedAt: null, 
      timeSpent: 0,
      score: 0,
      passed: null,
      status: 'started',
      answers: [],
      gradedAt: null,
      isManuallyGraded: false,
      manualScore: null,
      gradedBy: null,
      gradingNotes: ''
    };

    // Now, perform a single update to the existing document.
    await updateDoc(latestAttemptDocRef, {
      ...newAttempt,
      // Use arrayUnion to safely append the old attempt to the previousAttempts array.
      previousAttempts: arrayUnion(latestWithoutHistory)
    });
  }
    /**
   * Updates a specific answer within an ongoing assessment attempt.
   * This is called as the user answers each question.
   * @param attemptId The ID of the current assessment attempt.
   * @param newAnswer The AssessmentAnswer object for the current question.
   */
    async updateAttemptAnswer(attemptId: string, newAnswer: AssessmentAnswer): Promise<void> {
      const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);

      try {
        // Fetch the current attempt to get its existing answers
        const attemptSnapshot = await getDoc(attemptDocRef);
        if (!attemptSnapshot.exists()) {
          return;
        }

        const currentAttemptData = attemptSnapshot.data() as AssessmentAttempt;
        let updatedAnswers: AssessmentAnswer[] = currentAttemptData.answers || [];

        // Find if an answer for this question already exists based on assessmentQuestionId
        const existingAnswerIndex = updatedAnswers.findIndex(ans => ans.assessmentQuestionId === newAnswer.assessmentQuestionId);

        if (existingAnswerIndex > -1) {
          // Update existing answer
          updatedAnswers[existingAnswerIndex] = { ...newAnswer, answeredAt: Timestamp.now() };
        } else {
          // Add new answer
          updatedAnswers.push({ ...newAnswer, answeredAt: Timestamp.now() });
        }

        // Update only the 'answers' field in the document
        await setDoc(attemptDocRef, { answers: updatedAnswers }, { merge: true });
      } catch (error) {
        this.messagesService.error('Something went wrong while updating the attempt answer.');
        throw error;
      }
    }

  /**
   * Auto-grades a stuck attempt that was submitted for manual review
   * but the assessment no longer requires manual grading.
   */
  async autoGradeStuckManualAttempt(
    attemptId: string,
    answers: AssessmentAnswer[],
    totalQuestions: number,
    questions: AssessmentQuestion[],
    minimumPassingGrade: number
  ): Promise<void> {
    const attemptDocRef = doc(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts/${attemptId}`);

    const questionsMap = new Map<string, AssessmentQuestion>();
    questions.forEach(q => questionsMap.set(q.id, q));

    let score = 0;
    (answers || []).forEach(answer => {
      if (answer.type === 'text-options' || answer.type === 'image-options') {
        const question = questionsMap.get(answer.assessmentQuestionId);
        if (question) {
          score += this.calculatePartialScore(
            answer as TextOptionsAssessmentAnswer | ImageOptionsAssessmentAnswer,
            question as TextOptionsQuestion | ImageOptionsQuestion
          );
        }
      } else {
        score += answer.isCorrect ? 1 : 0;
      }
    });

    const passed = (score / totalQuestions) * 100 >= minimumPassingGrade;

    await updateDoc(attemptDocRef, {
      score,
      passed,
      status: passed ? 'completed' : 'failed',
      gradedAt: Timestamp.now(),
      gradedBy: 'auto',
      isManuallyGraded: false,
    });
  }

// This method should be in a separate service, e.g., AssessmentReviewService
async getQuestionsForReview(
  tenantId: string,
  latestAttempt: LatestAssessmentAttempt
): Promise<AssessmentQuestion[]> {
  const questionIds = latestAttempt.answers.map(
    (answer) => answer.assessmentQuestionId
  );

  if (questionIds.length === 0) {
    return [];
  }

  const BATCH_SIZE = 10;
  const questionIdBatches: string[][] = [];

  for (let i = 0; i < questionIds.length; i += BATCH_SIZE) {
    questionIdBatches.push(questionIds.slice(i, i + BATCH_SIZE));
  }

  const questionsMap = new Map<string, AssessmentQuestion>();

  for (const batch of questionIdBatches) {
    const batchQuerySnapshot = await getDocs(
      query(
        collection(this.firestore, `schools/${tenantId}/assessmentQuestions`),
        // Do NOT use a where('isDeleted', '==', false),filter here.
        // This is crucial for historical review.
        where("id", "in", batch)
      )
    );
    batchQuerySnapshot.forEach((doc) => {
      questionsMap.set(doc.id, {
        id: doc.id,
        ...doc.data(),
      } as AssessmentQuestion);
    });
  }

  // Now, re-order the questions based on the original attempt order
  const orderedQuestions = questionIds
    .map((id) => questionsMap.get(id))
    .filter((q) => !!q);

  return orderedQuestions as AssessmentQuestion[];
}
}
